import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { invokeGoogleCalendar } from "@/lib/google-calendar-invoke";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  FileText,
  Loader2,
  Trash2,
  CalendarClock,
  ExternalLink,
  ShoppingBag,
  Globe,
  UserPlus,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LeadDetailDrawer } from "@/components/leads/LeadDetailDrawer";
import type { Lead as LeadType } from "@/types/lead";
import { LinkAppointmentLeadDialog } from "@/components/appointments/LinkAppointmentLeadDialog";


interface Lead {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  current_appointment_id?: string;
  current_appointment_date?: string;
  current_appointment_duration?: number;
  stage: string;
  notes?: string;
  metadata?: any;
  appointmentId?: string;
}

interface AppointmentDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  workspaceId: string;
  onUpdate: () => void;
  onReschedule: (lead: Lead, eventMeta?: {
    appointmentId?: string;
    google_event_id?: string;
    duration_minutes?: number;
    service_name?: string;
    reason?: string;
    notes?: string;
    scheduled_date?: string;
  }) => void;
  hasGoogleCalendar?: boolean;
  googleCalendarId?: string | null;
  calendarDisplayName?: string | null;
  // Standalone event data (when lead is null)
  eventData?: {
    appointmentId: string;
    scheduled_date: string;
    duration_minutes: number;
    google_event_id?: string;
    google_calendar_id?: string | null;
    calendarDisplayName?: string | null;
    summary?: string;
    attendee_name?: string;
    attendee_email?: string;
    origin_source?: string;
    sync_status?: string;
    service_name?: string;
    notes?: string;
  };
}

export function AppointmentDetailsSheet({
  open,
  onOpenChange,
  lead,
  workspaceId,
  onUpdate,
  onReschedule,
  hasGoogleCalendar = false,
  googleCalendarId: googleCalendarIdProp,
  calendarDisplayName: calendarDisplayNameProp,
  eventData,
}: AppointmentDetailsSheetProps) {
  const { toast } = useToast();
  const [cancelling, setCancelling] = useState(false);
  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [linkLeadOpen, setLinkLeadOpen] = useState(false);

  const [overlapInfo, setOverlapInfo] = useState<{ eventName: string } | null>(null);
  const [resolvingConflict, setResolvingConflict] = useState(false);

  // Determine appointment details from lead or eventData
  const isStandalone = !lead && !!eventData;
  const appointmentDateStr = lead?.current_appointment_date || eventData?.scheduled_date;

  if (!appointmentDateStr) {
    return null;
  }

  const appointmentDate = new Date(appointmentDateStr);
  const durationMinutes = lead?.current_appointment_duration || eventData?.duration_minutes || 60;
  const endDate = new Date(appointmentDate.getTime() + durationMinutes * 60 * 1000);
  const serviceName = lead?.metadata?.last_service_name as string | undefined || eventData?.service_name;
  const displayName = lead?.name ?? eventData?.summary ?? eventData?.attendee_name ?? "Compromisso";
  const googleEventId = lead?.current_appointment_id || eventData?.google_event_id;
  const appointmentId = lead?.appointmentId || eventData?.appointmentId;
  const resolvedGoogleCalendarId =
    googleCalendarIdProp ?? eventData?.google_calendar_id ?? null;
  const resolvedCalendarDisplayName =
    calendarDisplayNameProp ?? eventData?.calendarDisplayName ?? null;

  const stageColors: Record<string, string> = {
    novo: "bg-blue-500",
    qualificado: "bg-green-500",
    proposta: "bg-purple-500",
    negociacao: "bg-orange-500",
    fechado: "bg-red-500",
    atendimento_humano: "bg-orange-500",
    ganho: "bg-green-500",
    perdido: "bg-red-500",
  };

  const originLabels: Record<string, string> = {
    manual: "Manual",
    ai: "IA",
    google_import: "Google Calendar",
  };

  const handleResolveConflict = async (resolution: 'link_to_google' | 'keep_local' | 'delete_local') => {
    if (!eventData?.appointmentId) return;
    setResolvingConflict(true);
    try {
      const { data, error } = await supabase.functions.invoke("google-calendar-sync", {
        body: {
          action: "resolve_conflict",
          workspaceId,
          appointmentId: eventData.appointmentId,
          resolution,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.message || data.error);
      toast({
        title: "Conflito resolvido",
        description: resolution === 'link_to_google' ? "Vinculado ao Google Calendar." :
          resolution === 'keep_local' ? "Mantido como local." : "Registro removido.",
      });
      onOpenChange(false);
      onUpdate();
    } catch (err: any) {
      console.error("Error resolving conflict:", err);
      toast({ title: "Erro ao resolver conflito", description: err.message, variant: "destructive" });
    } finally {
      setResolvingConflict(false);
    }
  };

  const handleSyncToGoogleCalendar = async (allowOverlapping = false) => {
    setSyncing(true);
    try {
      const endTime = new Date(appointmentDate.getTime() + durationMinutes * 60 * 1000);

      const summary = serviceName
        ? `${serviceName} - ${displayName}`
        : `Agendamento - ${displayName}`;

      const r = await invokeGoogleCalendar<any>("create_event", workspaceId, {
        summary,
        startTime: appointmentDate.toISOString(),
        endTime: endTime.toISOString(),
        attendeeEmail: lead?.email || eventData?.attendee_email || "",
        attendeeName: displayName,
        customerPhone: lead?.phone || "",
        serviceItem: serviceName || "",
        description: lead?.notes || eventData?.notes || "",
        allowOverlapping,
      });

      if (!r.ok) {
        if (r.kind === "slot_busy") {
          const eventName = (r.message || "")
            .replace("Event overlaps with existing appointment: ", "")
            .trim() || "outro evento";
          setOverlapInfo({ eventName });
          setSyncing(false);
          return;
        }
        if (r.kind === "needs_reconnect") {
          toast({
            title: "Reconecte o Google Calendar",
            description: r.message,
            variant: "destructive",
          });
          setSyncing(false);
          return;
        }
        throw new Error(r.message);
      }

      const data: any = r.data;
      const newEventId = data?.eventId ?? data?.event?.id;
      const newCalendarId = data?.calendarId ?? null;
      const newICalUid = data?.iCalUID ?? null;

      if (newEventId && lead) {
        await supabase
          .from("leads")
          .update({ current_appointment_id: newEventId })
          .eq("id", lead.id)
          .eq("workspace_id", workspaceId);
      }

      // Update local appointment row with sync identifiers (constraint-safe)
      if (newEventId && appointmentId) {
        await supabase
          .from("appointments")
          .update({
            google_event_id: newEventId,
            google_calendar_id: newCalendarId,
            google_ical_uid: newICalUid,
            sync_status: newCalendarId ? "synced" : "local_only",
            last_synced_at: new Date().toISOString(),
          } as any)
          .eq("id", appointmentId)
          .eq("workspace_id", workspaceId);
      }

      toast({
        title: "Sincronizado com Google Calendar",
        description: "O agendamento foi adicionado ao Google Calendar.",
      });

      setOverlapInfo(null);
      onUpdate();
    } catch (error) {
      console.error("Error syncing to Google Calendar:", error);
      toast({
        title: "Erro ao sincronizar",
        description: "Não foi possível sincronizar com o Google Calendar.",
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleCancelAppointment = async () => {
    setCancelling(true);
    try {
      // 1. Cancel Google Calendar event
      let googleEventIdToCancel = googleEventId;

      if (appointmentId) {
        const { data: apptData } = await supabase
          .from("appointments")
          .select("google_event_id")
          .eq("id", appointmentId)
          .eq("workspace_id", workspaceId)
          .single();
        if (apptData?.google_event_id) {
          googleEventIdToCancel = apptData.google_event_id;
        }
      }

      if (googleEventIdToCancel) {
        try {
          const r = await invokeGoogleCalendar("cancel_event", workspaceId, {
            eventId: googleEventIdToCancel,
          });
          if (!r.ok && r.kind !== "fatal") {
            console.warn("cancel_event non-fatal warning:", r.kind, r.message);
          }
        } catch (calError) {
          console.error("Error deleting calendar event:", calError);
        }
      }

      // 2. Cancel reminders (only if has lead)
      if (lead) {
        await supabase
          .from("appointment_reminders")
          .update({ status: "cancelled" })
          .eq("lead_id", lead.id)
          .eq("workspace_id", workspaceId)
          .eq("appointment_date", appointmentDateStr!)
          .eq("status", "pending");
      }

      // 3. Cancel appointment record
      if (appointmentId) {
        await supabase
          .from("appointments")
          .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
          .eq("id", appointmentId)
          .eq("workspace_id", workspaceId);
      } else if (lead) {
        await supabase
          .from("appointments")
          .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
          .eq("lead_id", lead.id)
          .eq("workspace_id", workspaceId)
          .eq("status", "scheduled")
          .eq("scheduled_date", appointmentDateStr!);
      }

      // 4. Clear lead's current_appointment fields (only if has lead and matches)
      if (lead) {
        const { data: leadData } = await supabase
          .from("leads")
          .select("current_appointment_date")
          .eq("id", lead.id)
          .eq("workspace_id", workspaceId)
          .single();

        if (leadData?.current_appointment_date === appointmentDateStr) {
          await supabase
            .from("leads")
            .update({
              current_appointment_date: null,
              current_appointment_id: null,
              current_appointment_duration: null,
            })
            .eq("id", lead.id)
            .eq("workspace_id", workspaceId);
        }
      }

      toast({
        title: "Agendamento cancelado",
        description: `O agendamento de ${displayName} foi cancelado.`,
      });

      onOpenChange(false);
      onUpdate();
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      toast({
        title: "Erro ao cancelar",
        description: "Não foi possível cancelar o agendamento.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleReschedule = () => {
    onOpenChange(false);
    if (lead) {
      onReschedule(lead, {
        appointmentId: lead.appointmentId,
        google_event_id: lead.current_appointment_id,
        duration_minutes: lead.current_appointment_duration,
        service_name: (lead.metadata?.last_service_name as string) || undefined,
        reason: undefined,
        notes: lead.notes,
        scheduled_date: lead.current_appointment_date,
      });
    } else if (eventData) {
      // For standalone events, create a minimal lead-like object for the reschedule flow
      const pseudoLead: Lead = {
        id: "",
        name: displayName,
        email: eventData.attendee_email,
        stage: "novo",
        notes: eventData.notes,
        appointmentId: eventData.appointmentId,
        current_appointment_date: eventData.scheduled_date,
        current_appointment_id: eventData.google_event_id,
        current_appointment_duration: eventData.duration_minutes,
      };
      onReschedule(pseudoLead, {
        appointmentId: eventData.appointmentId,
        google_event_id: eventData.google_event_id,
        duration_minutes: eventData.duration_minutes,
        service_name: eventData.service_name,
        notes: eventData.notes,
        scheduled_date: eventData.scheduled_date,
      });
    }
  };

  const handleViewLead = () => {
    setLeadDrawerOpen(true);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Detalhes do Agendamento
          </SheetTitle>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Date & Time */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-lg font-medium">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              {format(appointmentDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </div>
            <div className="flex items-center gap-3 text-lg">
              <Clock className="w-5 h-5 text-muted-foreground" />
              {format(appointmentDate, "HH:mm")} - {format(endDate, "HH:mm")} ({durationMinutes >= 60 ? `${Math.floor(durationMinutes / 60)}h${durationMinutes % 60 > 0 ? ` ${durationMinutes % 60}min` : ''}` : `${durationMinutes}min`})
            </div>
          </div>

          {/* Service */}
          {serviceName && (
            <div className="flex items-center gap-3 text-sm">
              <ShoppingBag className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{serviceName}</span>
            </div>
          )}

          {/* Lead Info or Standalone Info */}
          {lead ? (
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <User className="w-4 h-4" />
                Informações do Lead
              </h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-lg">{lead.name}</span>
                  <Badge className={`${stageColors[lead.stage] || "bg-gray-500"} text-white`}>
                    {lead.stage}
                  </Badge>
                </div>

                {lead.email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    {lead.email}
                  </div>
                )}

                {lead.phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    {lead.phone}
                  </div>
                )}

                {lead.notes && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <FileText className="w-4 h-4 mt-0.5" />
                    <span>{lead.notes}</span>
                  </div>
                )}
              </div>
            </div>
          ) : isStandalone && (
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <CalendarClock className="w-4 h-4" />
                Compromisso
              </h3>

              <div className="space-y-3">
                <span className="font-medium text-lg">{displayName}</span>

                {eventData?.origin_source && (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      <Globe className="w-3 h-3 mr-1" />
                      {originLabels[eventData.origin_source] || eventData.origin_source}
                    </Badge>
                  </div>
                )}

                {eventData?.attendee_name && eventData.attendee_name !== displayName && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="w-4 h-4" />
                    {eventData.attendee_name}
                  </div>
                )}

                {eventData?.attendee_email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    {eventData.attendee_email}
                  </div>
                )}

                {eventData?.notes && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <FileText className="w-4 h-4 mt-0.5" />
                    <span>{eventData.notes}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Google Calendar Info */}
          {(googleEventId || resolvedGoogleCalendarId) && (
            <div className="border-t pt-4 space-y-2">
              {googleEventId && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CalendarClock className="w-4 h-4" />
                  <span>Sincronizado com Google Calendar</span>
                </div>
              )}
              {resolvedGoogleCalendarId && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>
                    <span className="text-muted-foreground">Agenda: </span>
                    <span className="font-medium">
                      {resolvedCalendarDisplayName || resolvedGoogleCalendarId}
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Vincular compromisso avulso a um lead (habilita lembretes) */}
          {isStandalone && appointmentId && (
            <div className="border-t pt-4 space-y-2">
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => setLinkLeadOpen(true)}
              >
                <UserPlus className="w-4 h-4" />
                Vincular a um lead
              </Button>
              <p className="text-xs text-muted-foreground">
                Vincule para enviar lembretes automáticos deste agendamento.
              </p>
            </div>
          )}

          {/* Sync to Google Calendar button */}

          {!googleEventId && hasGoogleCalendar && eventData?.sync_status !== 'conflict' && (
            <div className="border-t pt-4">
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => handleSyncToGoogleCalendar()}
                disabled={syncing}
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CalendarClock className="w-4 h-4" />
                )}
                Sincronizar com Google Calendar
              </Button>
            </div>
          )}

          {/* Conflict resolution UI */}
          {isStandalone && eventData?.sync_status === 'conflict' && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-yellow-700 dark:text-yellow-300">
                <CalendarClock className="w-4 h-4" />
                Conflito de sincronização
              </div>
              <p className="text-xs text-muted-foreground">
                Este evento pode ser duplicado. Escolha como resolver:
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2"
                  disabled={resolvingConflict}
                  onClick={() => handleResolveConflict('link_to_google')}
                >
                  {resolvingConflict ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
                  Vincular ao Google
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2"
                  disabled={resolvingConflict}
                  onClick={() => handleResolveConflict('keep_local')}
                >
                  {resolvingConflict ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                  Manter local
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2 text-destructive hover:text-destructive"
                  disabled={resolvingConflict}
                  onClick={() => handleResolveConflict('delete_local')}
                >
                  {resolvingConflict ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Remover duplicata
                </Button>
              </div>
            </div>
          )}
        </div>

        <SheetFooter className="flex-col sm:flex-row gap-2">
          {lead && (
            <Button variant="outline" className="w-full sm:w-auto" onClick={handleViewLead}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Ver Lead
            </Button>
          )}

          <Button variant="outline" className="w-full sm:w-auto" onClick={handleReschedule}>
            <CalendarClock className="w-4 h-4 mr-2" />
            Reagendar
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="w-full sm:w-auto" disabled={cancelling}>
                {cancelling ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Cancelar
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancelar Agendamento</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja cancelar o agendamento de{" "}
                  <strong>{displayName}</strong> em{" "}
                  <strong>
                    {format(appointmentDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </strong>
                  ?
                  {googleEventId && (
                    <span className="block mt-2 text-orange-600">
                      O evento também será removido do Google Calendar.
                    </span>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Voltar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleCancelAppointment}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Confirmar Cancelamento
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </SheetFooter>

        {lead && (
          <LeadDetailDrawer
            open={leadDrawerOpen}
            onOpenChange={setLeadDrawerOpen}
            lead={{
              id: lead.id,
              name: lead.name,
              email: lead.email,
              phone: lead.phone,
              stage: lead.stage,
              notes: lead.notes,
              metadata: lead.metadata,
              current_appointment_date: lead.current_appointment_date,
              current_appointment_id: lead.current_appointment_id,
              created_at: "",
            }}
            workspaceId={workspaceId}
            onUpdate={onUpdate}
          />
        )}

        {isStandalone && appointmentId && (
          <LinkAppointmentLeadDialog
            open={linkLeadOpen}
            onOpenChange={setLinkLeadOpen}
            workspaceId={workspaceId}
            appointmentId={appointmentId}
            appointmentDate={appointmentDateStr!}
            durationMinutes={durationMinutes}
            googleEventId={googleEventId}
            onLinked={() => {
              onOpenChange(false);
              onUpdate();
            }}
          />
        )}

        {/* Overlap confirmation dialog */}

        <AlertDialog open={!!overlapInfo} onOpenChange={(open) => { if (!open) setOverlapInfo(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Conflito de Horário</AlertDialogTitle>
              <AlertDialogDescription>
                Já existe um evento no Google Calendar neste horário:{" "}
                <strong>{overlapInfo?.eventName}</strong>.
                <span className="block mt-2">Deseja criar o agendamento mesmo assim?</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setOverlapInfo(null);
                  handleSyncToGoogleCalendar(true);
                }}
              >
                Sincronizar mesmo assim
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
